import React, { useMemo, useState } from 'react';

import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { SearchClear } from '../../_Components';

const phoneKey = (item) => (
    item && item.department && item.specialist
        ? `${item.department}:${item.specialist}`
        : null
);

const toValue = (item) => ({
    blind: !!item.blind,
    department: item.department,
    specialist: item.specialist,
});

const flattenBook = (list = []) => {
    const items = [];
    const seen = new Set();
    (list || []).forEach((department, deptIndex) => {
        const departmentId = department._id || department.id || `dept-${deptIndex}`;
        (department.phonebook || []).forEach((phone, phoneIndex) => {
            const specialistId = phone._id || phone.id || `phone-${deptIndex}-${phoneIndex}`;
            const key = `${departmentId}:${specialistId}`;
            if (seen.has(key)) return;
            seen.add(key);
            items.push({
                key,
                specialist: specialistId,
                specialistName: phone.name || '',
                department: departmentId,
                departmentName: department.name || '',
                phone: phone.phone || '',
                blind: false,
            });
        });
    });
    return items;
};

const resolveSelected = (value, book) => {
    const seen = new Set();
    return (Array.isArray(value) ? value : [])
        .map((el) => {
            const found = book.find((item) => (
                item.department === el.department && item.specialist === el.specialist
            ));
            if (found) {
                return { ...found, blind: !!el.blind };
            }
            const key = phoneKey(el);
            if (!key) return null;
            return {
                key,
                specialist: el.specialist,
                specialistName: 'Не найден',
                department: el.department,
                departmentName: '',
                phone: '',
                blind: !!el.blind,
            };
        })
        .filter((item) => {
            if (!item || seen.has(item.key)) return false;
            seen.add(item.key);
            return true;
        });
};

const matchesQuery = (item, query) => {
    if (!query) return true;
    return `${item.specialistName} ${item.departmentName} ${item.phone}`.toLowerCase().includes(query);
};

const PhoneHead = () => (
    <div className="pc-xfer__phone-hd" aria-hidden="true">
        <span className="pc-xfer__phone-name">Сотрудник</span>
        <span className="pc-xfer__phone-dept">Отдел</span>
        <span className="pc-xfer__phone-num">Телефон</span>
        <span className="pc-xfer__phone-ico" />
    </div>
);

const PhoneRow = ({ item, selected, onClick }) => (
    <button
        type="button"
        className={`pc-xfer__item pc-xfer__phone${selected ? ' is-on' : ''}`}
        onClick={onClick}
    >
        <span className="pc-xfer__phone-name">{item.specialistName || '—'}</span>
        <span className="pc-xfer__phone-dept">{item.departmentName || '—'}</span>
        <span className="pc-xfer__phone-num">{item.phone || ''}</span>
        {selected
            ? <CloseIcon sx={{ fontSize: 15 }} />
            : <AddIcon sx={{ fontSize: 16 }} />}
    </button>
);

const PhonesInput = ({ input: { value, onChange }, list }) => {
    const book = useMemo(() => flattenBook(list), [list]);
    const selected = resolveSelected(value, book);
    const selectedKeys = new Set(selected.map((item) => item.key));
    const available = book.filter((item) => !selectedKeys.has(item.key));

    const [query, setQuery] = useState('');
    const [dept, setDept] = useState('all');

    const q = query.trim().toLowerCase();
    const departments = useMemo(() => {
        const rows = [];
        book.forEach((item) => {
            if (!rows.some((row) => row.id === item.department)) {
                rows.push({ id: item.department, name: item.departmentName });
            }
        });
        return rows;
    }, [book]);

    const visible = available.filter((item) => {
        if (dept && dept !== 'all' && item.department !== dept) return false;
        return matchesQuery(item, q);
    });

    const apply = (next) => onChange(next.map(toValue));
    const addOne = (item) => apply(selected.concat([item]));
    const removeOne = (item) => apply(selected.filter((row) => row.key !== item.key));
    const addVisible = () => {
        if (!visible.length) return;
        apply(selected.concat(visible));
    };
    const clearRight = () => {
        if (!selected.length) return;
        apply([]);
    };

    return (
        <div className="pc-xfer">
            <p className="pc-xfer__hint">
                Нажмите строку, чтобы добавить или убрать. Можно искать по имени, отделу или номеру.
            </p>
            <div className="pc-xfer__tools">
                <div className="pc-xfer__searchfield">
                    <label className="pc-field__label">Поиск</label>
                    <div className="pc-pick-srch">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <circle cx="7" cy="7" r="4.5" />
                            <path d="M10.5 10.5L14 14" />
                        </svg>
                        <input
                            className="pc-pick-inp"
                            placeholder="Имя, отдел или телефон"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                        <SearchClear visible={Boolean(query)} onClear={() => setQuery('')} />
                    </div>
                </div>
                {departments.length > 1 && (
                    <div className="pc-xfer__deptfield">
                        <label className="pc-field__label">Отдел</label>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            value={dept}
                            onChange={(event) => setDept(event.target.value)}
                        >
                            <MenuItem value="all">Все отделы</MenuItem>
                            {departments.map((row) => (
                                <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>
                            ))}
                        </TextField>
                    </div>
                )}
            </div>
            <div className="pc-xfer__cols">
                <div className="pc-xfer__col">
                    <div className="pc-xfer__hd">
                        <b>Доступные</b>
                        <span>
                            {visible.length === available.length
                                ? available.length
                                : `${visible.length} из ${available.length}`}
                        </span>
                    </div>
                    <PhoneHead />
                    <div className="pc-xfer__list pc-xfer__list--phones">
                        {visible.map((item) => (
                            <PhoneRow key={item.key} item={item} onClick={() => addOne(item)} />
                        ))}
                        {visible.length === 0 && (
                            <div className="pc-xfer__empty">
                                {book.length === 0
                                    ? 'В справочнике нет телефонов'
                                    : available.length === 0
                                        ? 'Все уже выбраны'
                                        : 'Ничего не найдено'}
                            </div>
                        )}
                    </div>
                    <button type="button" className="pc-xfer__all" disabled={!visible.length} onClick={addVisible}>
                        Добавить все{q || (dept && dept !== 'all') ? ' найденные' : ''}
                    </button>
                </div>
                <div className="pc-xfer__col">
                    <div className="pc-xfer__hd">
                        <b>Выбранные</b>
                        <span>{selected.length}</span>
                    </div>
                    <PhoneHead />
                    <div className="pc-xfer__list pc-xfer__list--phones">
                        {selected.map((item) => (
                            <PhoneRow
                                key={item.key}
                                item={item}
                                selected
                                onClick={() => removeOne(item)}
                            />
                        ))}
                        {selected.length === 0 && (
                            <div className="pc-xfer__empty">Пока ничего не выбрано — добавьте слева</div>
                        )}
                    </div>
                    <button type="button" className="pc-xfer__all" disabled={!selected.length} onClick={clearRight}>
                        Убрать все
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PhonesInput;
